
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
    Wallet, 
    ShieldCheck, 
    ArrowDownLeft, 
    Store, 
    CreditCard, 
    Send, 
    TrendingUp, 
    Bell, 
    Layers, 
    Wifi, 
    Battery, 
    ChevronRight, 
    Zap, 
    ArrowUpRight, 
    Repeat,
    Lock,
    Smartphone
} from "lucide-react";

const NotificationItem = ({ icon: Icon, title, desc, amount, delay }: any) => (
  <motion.div
    initial={{ opacity: 0, y: 20, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
    transition={{ delay, type: "spring", stiffness: 120, damping: 20 }}
    className="bg-white/90 backdrop-blur-xl p-3 rounded-md shadow-sm border border-stone-100 flex items-center gap-3 mb-2 w-full group hover:bg-white transition-colors cursor-pointer"
  >
    <div className="w-8 h-8 rounded-md flex items-center justify-center text-white shadow-sm bg-[#0A0B0F] group-hover:scale-105 transition-transform">
        <Icon size={14} strokeWidth={2} />
    </div>
    <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold text-[#0A0B0F] truncate">{title}</div>
        <div className="text-[9px] text-gray-500 truncate">{desc}</div>
    </div>
    {amount && <div className="text-[10px] font-mono font-bold text-[#0A0B0F]">{amount}</div>}
  </motion.div>
);

const MobileGhostCard = () => {
    const GHOST_ICON = "https://raw.githubusercontent.com/ghost-protocol-labs/ghost-token-sui/refs/heads/main/assets/GHOST.svg";
    const SOL_ICON = "https://cryptologos.cc/logos/solana-sol-logo.svg?v=031";
    const SUI_ICON = "https://cryptologos.cc/logos/sui-sui-logo.svg?v=031";
    const USDC_ICON = "https://cryptologos.cc/logos/usd-coin-usdc-logo.svg?v=031";

    return (
        <div className="relative w-full aspect-[1.586/1] bg-[#0A0B0F] rounded-md border border-white/10 p-5 overflow-hidden shadow-2xl group cursor-pointer transform transition-transform hover:scale-[1.02]">
            {/* Elite Matte Obsidian Finish */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#080808] to-[#121212]"></div>
            <div className="absolute inset-0 opacity-[0.04] bg-[url('https://www.transparenttextures.com/patterns/brushed-alum.png')]"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-nobel-gold/20 rounded-full blur-[50px] -translate-y-1/2 translate-x-1/2"></div>
            
            {/* Sharp light streak */}
            <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-50"></div>

            <div className="relative z-10 h-full flex flex-col justify-between">
                <div className="flex justify-between items-start">
                    <div className="flex flex-col gap-1">
                        <div className="w-9 h-6 rounded-sm bg-gradient-to-br from-nobel-gold via-[#D4AF37] to-[#8A6D3B] shadow-lg border border-black/30 overflow-hidden relative">
                             <div className="absolute inset-0 opacity-20 bg-[repeating-linear-gradient(90deg,transparent,transparent_2px,#000_2px,#000_3px)]"></div>
                             <div className="w-full h-[0.5px] bg-black/10 absolute top-1/2"></div>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        <Wifi size={14} className="rotate-90 text-white/30" />
                        <div className="flex gap-1.5 opacity-20 group-hover:opacity-40 transition-opacity">
                            <img src={GHOST_ICON} className="w-2.5 h-2.5 grayscale invert" alt="" />
                            <img src={SOL_ICON} className="w-2.5 h-2.5 grayscale invert" alt="" />
                            <img src={SUI_ICON} className="w-2.5 h-2.5 grayscale invert" alt="" />
                            <img src={USDC_ICON} className="w-2.5 h-2.5 grayscale invert" alt="" />
                        </div>
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="text-lg font-mono text-white tracking-[0.2em] drop-shadow-2xl">
                        •••• 9201
                    </div>
                    <div className="flex justify-between items-end">
                        <div className="space-y-0.5">
                            <div className="text-[6px] uppercase tracking-[0.4em] font-bold text-white/30">Holder</div>
                            <div className="text-[9px] font-bold tracking-widest uppercase text-white/80">A. GHOST</div>
                        </div>
                        <div className="flex flex-col items-end">
                             <div className="italic font-serif font-bold text-sm text-white/90 leading-none">Ghost</div>
                             <div className="text-[6px] font-mono text-white/40 uppercase tracking-tighter mt-0.5">Titanium</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const PhoneMockup: React.FC = () => {
  const [notifications, setNotifications] = useState<any[]>([]);

  useEffect(() => {
    const sequence = [
        { id: 1, icon: Store, title: "Merchant Payment", desc: "Terminal #402", amount: "-$42.50" },
        { id: 2, icon: ShieldCheck, title: "Escrow Release", desc: "Contract #892", amount: "+$1,200" },
        { id: 3, icon: Layers, title: "Block Confirmed", desc: "Ghost Chain", amount: "0.2s" },
    ];

    let currentIndex = 0;
    setNotifications([sequence[0]]);

    const interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % (sequence.length + 1);
        if (currentIndex === 0) {
            setNotifications([]);
        } else {
             const nextItem = sequence[currentIndex - 1];
             setNotifications(prev => [nextItem, ...prev].slice(0, 3));
        }
    }, 2800);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative group select-none flex justify-center w-[320px] h-[660px]">
        <div 
            className="relative w-full h-full rounded-[48px] bg-gradient-to-b from-[#2D3142] via-[#15171C] to-black p-2"
            style={{ 
                boxShadow: '0 20px 60px -10px rgba(0, 0, 0, 0.5), 0 10px 20px -5px rgba(0, 0, 0, 0.2)' 
            }}
        >
            <div className="w-full h-full bg-black rounded-[40px] p-1.5">
                <div className="absolute right-[-2px] top-32 h-16 w-1 bg-stone-700 rounded-l-md opacity-60"></div>
                <div className="absolute left-[-2px] top-32 h-8 w-1 bg-stone-700 rounded-r-md opacity-60"></div>
                
                <div className="w-full h-full bg-black rounded-[32px] overflow-hidden relative">
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-transparent z-[60] pointer-events-none rounded-[32px]"></div>
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 h-[30px] w-[100px] bg-black rounded-full z-40 flex justify-center items-center shadow-lg transition-all duration-300">
                        <div className="w-1.5 h-1.5 rounded-full bg-stone-800 ml-auto mr-3"></div>
                    </div>

                    <div className="w-full h-full bg-[#F8F9FA] flex flex-col relative overflow-hidden font-sans">
                        
                        <div className="h-14 w-full flex justify-between items-center px-7 pt-4 z-20 text-[#0A0B0F]">
                            <span className="text-[10px] font-bold">9:41</span>
                            <div className="flex gap-1.5 items-center">
                                <Wifi size={12} strokeWidth={2.5} />
                                <Battery size={16} strokeWidth={2.5} />
                            </div>
                        </div>

                        <div className="flex-1 px-5 pt-4 flex flex-col gap-5 relative z-10 overflow-y-auto no-scrollbar pb-10">
                            
                            <div className="flex justify-between items-center px-1">
                                <div>
                                    <div className="font-serif font-bold text-2xl text-[#0A0B0F]">Ghost</div>
                                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Wallet</div>
                                </div>
                                <div className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#0A0B0F] shadow-sm">
                                    <Bell size={16} />
                                </div>
                            </div>

                            {/* Total Balance Card */}
                            <div className="bg-[#0A0B0F] text-white p-5 rounded-md shadow-2xl relative overflow-hidden shrink-0">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-b from-[#2D3142] to-transparent opacity-20 rounded-full blur-3xl -translate-y-10 translate-x-10"></div>
                                <div className="relative z-10">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-[9px] text-gray-500 font-bold uppercase tracking-[0.2em]">Total Balance</div>
                                        <TrendingUp size={12} className="text-emerald-500" />
                                    </div>
                                    <div className="flex flex-col mb-5">
                                        <div className="flex items-baseline gap-0.5">
                                            <span className="text-3xl font-serif font-medium tracking-tighter">$84,250</span>
                                            <span className="text-gray-500 text-xl">.00</span>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 border-t border-white/10 pt-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                                                <span className="text-[9px] font-bold text-gray-400">SUI</span>
                                            </div>
                                            <span className="text-[9px] font-mono font-bold">4,500</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-4 h-4 rounded-full bg-purple-500"></div>
                                                <span className="text-[9px] font-bold text-gray-400">SOL</span>
                                            </div>
                                            <span className="text-[9px] font-mono font-bold">145.2</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-4 gap-2 px-0.5 shrink-0">
                                {[
                                    { i: <ArrowUpRight size={18}/>, l: "Send" },
                                    { i: <ArrowDownLeft size={18}/>, l: "Deposit" },
                                    { i: <Repeat size={18}/>, l: "Swap" },
                                    { i: <ShieldCheck size={18}/>, l: "Vault" },
                                ].map((item, idx) => (
                                    <div key={idx} className="bg-white rounded-md border border-stone-100 shadow-sm flex flex-col items-center justify-center gap-2 h-[72px] hover:border-nobel-gold transition-all duration-300 cursor-pointer group active:scale-95">
                                        <div className="text-gray-400 group-hover:text-[#0A0B0F] transition-colors">{item.i}</div>
                                        <span className="text-[8px] font-bold text-gray-400 group-hover:text-[#0A0B0F] uppercase tracking-wide">{item.l}</span>
                                    </div>
                                ))}
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-3 px-1">
                                    <div className="text-[10px] font-bold text-[#0A0B0F] uppercase tracking-widest">Active Ghost Card</div>
                                </div>
                                <MobileGhostCard />
                                <div className="grid grid-cols-3 gap-2 mt-3">
                                    <button className="flex items-center justify-center gap-1 p-2 rounded-md bg-white border border-stone-100 text-[9px] font-bold uppercase tracking-wider text-stone-500 hover:text-stone-900 transition-colors">
                                        <Lock size={10} /> Freeze
                                    </button>
                                    <button className="flex items-center justify-center gap-1 p-2 rounded-md bg-white border border-stone-100 text-[9px] font-bold uppercase tracking-wider text-stone-500 hover:text-stone-900 transition-colors">
                                        <CreditCard size={10} /> Details
                                    </button>
                                    <button className="flex items-center justify-center gap-1 p-2 rounded-md bg-white border border-stone-100 text-[9px] font-bold uppercase tracking-wider text-stone-500 hover:text-stone-900 transition-colors">
                                        <Smartphone size={10} /> Wallet
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 min-h-0">
                                <div className="text-[10px] font-bold text-[#0A0B0F] uppercase tracking-widest mb-3 px-1">Security Ledger</div>
                                <div className="space-y-2 relative h-[120px] overflow-hidden">
                                    <AnimatePresence mode="popLayout" initial={false}>
                                        {notifications.map((n) => (
                                            <NotificationItem key={n.id} {...n} />
                                        ))}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </div>

                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-32 h-1 bg-black/60 rounded-full z-30"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
};
